import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';
import type { HTMLAttributes, ReactNode } from 'react';

/**
 * Thin reusable wrappers over the repo's dnd-kit sortable idiom (see
 * OrderAnswer) for the builder's reorderable lists — pages, slide blocks,
 * answer options. The drag handle is exposed to the row so each list keeps
 * its own layout; existing up/down buttons remain as the keyboard/touch
 * fallback, the same convention the player uses.
 */

/** Immutable splice-move; the single reorder primitive the lists share. */
export function reorder<T>(list: readonly T[], from: number, to: number): T[] {
  const next = [...list];
  const [moved] = next.splice(from, 1);
  if (moved === undefined) return [...list];
  next.splice(to, 0, moved);
  return next;
}

export function SortableList({
  ids,
  onReorder,
  className,
  children,
}: {
  ids: string[];
  onReorder: (from: number, to: number) => void;
  className?: string;
  children: ReactNode;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  function onDragEnd(e: DragEndEvent) {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const from = ids.indexOf(String(active.id));
    const to = ids.indexOf(String(over.id));
    if (from === -1 || to === -1) return;
    onReorder(from, to);
  }
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        {className ? <div className={className}>{children}</div> : children}
      </SortableContext>
    </DndContext>
  );
}

export function SortableRow({
  id,
  className,
  children,
}: {
  id: string;
  /** Static classes, or a function of the dragging state. */
  className?: string | ((isDragging: boolean) => string);
  children: (drag: { dragHandle: HTMLAttributes<HTMLElement>; isDragging: boolean }) => ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });
  const cls = typeof className === 'function' ? className(isDragging) : (className ?? '');
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cls}
    >
      {children({
        dragHandle: { ...attributes, ...listeners } as HTMLAttributes<HTMLElement>,
        isDragging,
      })}
    </div>
  );
}

/** Standard grip handle; spread the row's dragHandle onto it. */
export function DragHandle({
  dragHandle,
  label,
}: {
  dragHandle: HTMLAttributes<HTMLElement>;
  label: string;
}) {
  return (
    <span
      {...dragHandle}
      aria-label={label}
      className="shrink-0 touch-none cursor-grab text-fg-dim active:cursor-grabbing"
    >
      <GripVertical className="h-4 w-4" />
    </span>
  );
}
